import { CookieManager } from "@Utilities/CookieManager";
import { ContentService } from "@Api/ContentService";
import { ISpecialContent } from "@Api/ContentService";
import { intervalToDuration } from "date-fns";
import { Media } from '@Utilities/Media';
import { TextResource } from "@Utilities/TextResource";

interface ICell
{
	Element: HTMLElement
	UtcString: string;
	UtcDate: Date;
}

export class TransitBannerBase
{
	private Banner: HTMLElement;
	private Cells: ICell[];
	private Container: HTMLElement;
	private Content: ISpecialContent[];
	private InvalidFetches = 0;
	public TransitInterval: ReturnType<typeof setInterval>;

	constructor()
	{
	}

	public Initialize(): void
	{
		if (this.Container !== null)
		{
			this.Content.forEach(banner =>
			{
				if (banner.Element.indexOf("skin-customer-message") > -1)
				{
					this.Container.innerHTML = this.RefactorHtml(banner.Html);
				}
			});

			this.ConfigureBanner();
		}
	}

	private ConfigureBanner = (): void =>
	{
		this.Banner = this.Container.querySelector(".ttb");
		this.Cells = [];
		const bannerHeader = this.Container.querySelector(".ttb__header");
		const bannerHeadline = this.Container.querySelector(".ttb__headline");
		const bannerContent = this.Container.querySelector(".ttb__content");
		const cells: NodeListOf<HTMLElement> = this.Container.querySelectorAll("[data-cutoff-time]");

		if (Media.IsMobile)
		{
			bannerHeader.classList.add("collapsed");
			bannerContent.classList.remove("show");
		}
		cells.forEach(cell =>
		{
			const utcString = cell.getAttribute("data-cutoff-time");
			const utcDate = new Date(utcString);

			this.Cells.push({
				Element: cell,
				UtcDate: utcDate,
				UtcString: utcString
			});
		});

		this.UpdateCountdown();
		this.TransitInterval = setInterval(this.UpdateCountdown, 1000);

		if (this.Banner.classList.contains("ttb--6-20"))
		{
			bannerHeadline.innerHTML = `${TextResource.GetTextByCulture("Feature_TransitBanner_Headline_FathersDay")}&nbsp;`;
		}

		if (this.Banner.classList.contains("ttb--10-31"))
		{
			bannerHeadline.innerHTML = `${TextResource.GetTextByCulture("Feature_TransitBanner_Headline_Halloween")}&nbsp;`;
		}

		if (this.Banner.classList.contains("ttb--12-25"))
		{
			bannerHeadline.innerHTML = `${TextResource.GetTextByCulture("Feature_TransitBanner_Headline_Christmas")}&nbsp;`;
		}

		this.Container.classList.remove("transitLoad");

		if (CookieManager.GetCookie(CookieManager.Cookies.ReturnVisitor.Key, "Ordered") === "true")
		{
			const orderWrap = document.createElement("div");
			orderWrap.innerHTML = `<div class="ttb-track"><a href="/order-status.aspx"><img alt="Shipping Box" src="/content/images/box.svg" style="width: 20px; height: auto; margin-right: 5px; display: inline-block;vertical-align: middle;" /> <span style="vertical-align: middle;display: inline-block;">${TextResource.GetTextByCulture("Feature_Tracking_TrackOrder")}</span></a></div>`;
			this.Banner.after(orderWrap);
		}
	};

	private RefactorHtml = (html: string): string =>
	{
		html = html.replace("data-target", "data-bs-target");
		html = html.replace("data-toggle", "data-bs-toggle");
		html = html.replace("ttb__header btn btn-link btn-block", "ttb__header btn btn-link d-block w-100");
		html = html.replace(`<i class="ttb__arrow fa fas fa-chevron-down" aria-hidden="false"></i>`, `<svg class="ttb__arrow svg-inline--fa" aria-hidden="true"><use xlink:href="#fa-solid-fa-chevron-down"></use></svg>`);
		return html;
	};

	private UpdateCountdown = (): void =>
	{
		let fetchBanner = false;
		let invalidCells = 0;

		const currentDate = new Date();

		this.Cells.forEach((cell: ICell) => 
		{
			const duration = intervalToDuration({
				start: currentDate,
				end: cell.UtcDate
			});
			const days = duration.days || 0;
			const hours = duration.hours || 0;
			const minutes = duration.minutes || 0;
			const seconds = duration.seconds || 0;
			const daysString = days > 0 ? `${days.toString()} day${days > 1 ? "s" : ""} `: "";
			const hoursString = hours < 1 ? "00" : hours.toString().padStart(2, "0");
			const minutesString = minutes < 1 ? "00": minutes.toString().padStart(2, "0");
			const secondsString = seconds < 1 ? "00": seconds.toString().padStart(2, "0");

			// Add a class and change the countdown format if the next cutoff time is more than a day away
			if (days > 0 && !this.Banner.classList.contains("ttb--days"))
			{
				this.Banner.classList.add("ttb--days");
			}

			cell.Element.textContent = `${daysString}${hoursString}:${minutesString}:${secondsString}`;

			if (days < 1 && hours < 1 && minutes < 1 && seconds < 1)
			{
				if (this.InvalidFetches < 2)
				{
					// Fetch the banner if we have less than 3 invalid attempts (including the initial attempt)
					fetchBanner = true;

					if (days < 0 || hours < 0 || minutes < 0 || seconds < 0)
					{
						invalidCells = invalidCells + 1;
					}
				}
				else
				{
					// Stop attempting to fetch if we have more than 4 invalid fetches to avoid an infinite loop and pounding the servers
					clearInterval(this.TransitInterval);
				}
			}
		});

		if (fetchBanner)
		{
			// Stop the banner from updating when the timer hits zero
			clearInterval(this.TransitInterval);

			if (invalidCells > 0)
			{
				// Add to invalid fetch counter if there's any invalid cells
				this.InvalidFetches = this.InvalidFetches + 1;
			}
			else
			{
				// Reset fetch counter if all cells are valid
				this.InvalidFetches = 0;
			}

			window.setTimeout(() =>
			{
				// Wait three seconds and do another getcontent request to update the transit banner
				const contentService = new ContentService();
				contentService.ApiCall()
					.then(() =>
					{
						if (typeof ContentService !== "undefined" && typeof ContentService.Response !== "undefined")
						{
							// Reinitialize the transit banner with the new shipping information
							this.TriggerContentCallbacks();
						};
					})
					.catch(() =>
					{
						console.log("GetContent error when updating transit banner");
					});
			}, 3000);
		}
	};

	public TriggerContentCallbacks = (): void =>
	{
		if (typeof ContentService.Response !== "undefined")
		{
			this.Content = ContentService.Response.TransitTimeContent;
			this.Container = document.querySelector("#transitTimeBanner");

			if (this.Content !== undefined && this.Content !== null && this.Content.length > 0)
			{
				this.Initialize();
			}
			else if (this.Container !== null)
			{
				// If we have no more transit times left in this profile, delete the container from the dom
				this.Container.remove();
			}
		}
	};
}